import { zodResolver } from '@hookform/resolvers/zod';
import { Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { TextField } from '@/business';
import { Alert, Button, Field, Input } from '@/design-system';
import { demoAccount } from '@/shared/config/demo-account';
import { env } from '@/shared/config/env';
import { apiErrorMessage } from '@/shared/api';

import { useLogin } from '../api/auth';
import { AuthFrame } from '../components/AuthFrame';

const schema = z.object({
  email: z.string().trim().min(1, 'validation.email').pipe(z.email('validation.email')),
  password: z.string().min(1, 'validation.password'),
});
type Values = z.infer<typeof schema>;

export function LoginPage({ redirect }: { redirect?: string }) {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const login = useLogin();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: { email: '', password: '' } });
  const demo = demoAccount();

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const res = await login.mutateAsync(values);
      if (res.status === 'mfa_required') await navigate({ to: '/login/mfa', search: redirect ? { redirect } : {} });
      else if (res.status === 'mfa_enrolment_required') await navigate({ to: '/login/mfa-setup', search: redirect ? { redirect } : {} });
      else await navigate({ to: redirect ?? '/dashboard' });
    } catch (e) {
      setError(apiErrorMessage(e));
    }
  });

  return (
    <AuthFrame>
      <div className="flex flex-col gap-1">
        <h1 className="text-h1 text-ink">{t('login.title')}</h1>
        <p className="text-body text-ink-secondary">{t('login.purpose')}</p>
      </div>
      {error ? <Alert tone="negative" title={error} /> : null}
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        <TextField control={form.control} name="email" label={t('login.email')} autoComplete="username" dir="ltr" size="lg" />
        <PasswordField form={form} label={t('login.password')} />
        <div className="pt-1">
          <Button type="submit" variant="primary" size="lg" loading={login.isPending} className="w-full">
            {t('login.submit')}
          </Button>
        </div>
      </form>
      <Link to="/reset-password" className="text-body text-accent hover:underline">
        {t('login.forgot')}
      </Link>
      {env.apiMock ? (
        <div className="flex flex-col items-start gap-2 border-t border-border pt-4">
          <p className="text-meta text-ink-muted">{t('login.mockHint')}</p>
          {demo ? (
            <Button
              size="sm"
              onClick={() => {
                form.setValue('email', demo.email, { shouldValidate: true });
                form.setValue('password', demo.password, { shouldValidate: true });
              }}
            >
              {t('login.useDemoAdmin')}
            </Button>
          ) : null}
        </div>
      ) : null}
    </AuthFrame>
  );
}

function PasswordField({ form, label }: { form: UseFormReturn<Values>; label: string }) {
  const { t } = useTranslation('auth');
  const err = form.formState.errors.password?.message;
  return (
    <Field label={label} error={err ? t(err as 'validation.password') : undefined}>
      <Input type="password" autoComplete="current-password" dir="ltr" size="lg" {...form.register('password')} />
    </Field>
  );
}
